import { Typography } from "antd";
import { useTranslation } from "react-i18next";

import { useTypesAndSubTypes } from "@/components/Elements/Filters/useTypesAndSubTypes";
import TableTitleCell from "@/components/Elements/TableTitleCell";
import { useInterventionType } from "@/entities/referential/referential-intervention-types";
import { REFERENTIAl_STATES_ENDPOINT } from "@/entities/referential/referential-states";
import { InterventionTemplatesListTableRowActions } from "@/modules/intervention-templates/intervention-templates-list-table";
import ListTable from "@/modules/list-table";
import { ModelLink } from "@/modules/model-link";
import { ModelList } from "@/modules/model-list";
import { StateTag } from "@/modules/state-tag";
import {
  getTableFilterQueryable,
  TABLE_FILTER_TEXT,
  useTableFilterReferentialStates,
} from "@/modules/table-filter";
import { format } from "@/utilities/date";

const InterventionTemplatesTable = (listTableProps) => {
  const { t } = useTranslation();

  const { types } = useInterventionType();
  const { typesFilter } = useTypesAndSubTypes();
  const statesFilter = useTableFilterReferentialStates(
    REFERENTIAl_STATES_ENDPOINT.INTERVENTIONTEMPLATE
  );

  const columns = [
    {
      title: t("global:name"),
      dataIndex: "name",
      render: (_name, interventionTemplate) => (
        <TableTitleCell tooltip={interventionTemplate.name} maxWidth="40vw">
          <ModelLink model="interventiontemplate" item={interventionTemplate} />
        </TableTitleCell>
      ),
      sorter: true,
      fixed: "left",
      minWidth: 220,
      ...TABLE_FILTER_TEXT,
    },
    {
      title: t("interventionTemplate:type:label"),
      dataIndex: "intervention_type_id",
      render: (intervention_type_id) => (
        <Typography.Text>
          {types.find(({ id }) => id === intervention_type_id)?.name}
        </Typography.Text>
      ),
      ...typesFilter,
    },
    {
      title: t("interventionTemplate:description:label"),
      dataIndex: "description",
      render: (description) => <Typography.Text>{description}</Typography.Text>,
      sorter: true,
      ...TABLE_FILTER_TEXT,
    },
    {
      title: t("interventionTemplate:version:currentVersion"),
      dataIndex: "version",
      render: (version) => <Typography.Text>{`v${version}`}</Typography.Text>,
    },
    {
      title: t("interventionTemplate:referenceId"),
      dataIndex: "reference_id",
      render: (reference_id, { id }) => (
        <Typography.Text>{reference_id ? reference_id : id}</Typography.Text>
      ),
    },
    {
      title: t("global:interventionPlural"),
      dataIndex: "interventions_count",
      sorter: true,
      render: (interventions_count) => (
        <Typography.Text>{interventions_count}</Typography.Text>
      ),
    },
    {
      title: t("global:status"),
      dataIndex: "state",
      render: (state) => <StateTag value={state} type="interventiontemplate" />,
      sorter: true,
      ...statesFilter,
    },
    {
      title: t("global:organizationPlural"),
      dataIndex: ["organizations", "id"],
      render: (_, { organizations }) => (
        <ModelList model="organization" items={organizations} withLink />
      ),
      ...getTableFilterQueryable({ model: "organizations" }),
    },
    {
      title: t("global:assetTypePlural"),
      dataIndex: "asset_types",
      render: (_, { asset_types }) => (
        <ModelList model="assettype" items={asset_types} withLink />
      ),
      ...getTableFilterQueryable({ model: "assetTypes" }),
      key: "assetTypes.id",
    },
    {
      title: t("global:modulePlural"),
      dataIndex: ["modules", "id"],
      render: (_, { modules }) => <ModelList model="module" items={modules} />,
      ...getTableFilterQueryable({ model: "modules", fields: [] }),
    },
    {
      title: t("global:lastUpdate"),
      dataIndex: "updated_at",
      sorter: true,
      render: (value) => format(value),
    },
    {
      title: "",
      fixed: "right",
      render: (template) => (
        <InterventionTemplatesListTableRowActions template={template} />
      ),
    },
  ];

  return <ListTable columns={columns} {...listTableProps} />;
};

export default InterventionTemplatesTable;
